import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { SquaresFour, Package as PackageIcon, EnvelopeSimple, SignOut, Plus, PencilSimple, Trash, Phone, CreditCard, Images, Megaphone, CopySimple, WhatsappLogo } from "@phosphor-icons/react";
import { api, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PackageForm from "@/components/PackageForm";

const STATUS = ["new", "contacted", "converted", "closed"];
const STATUS_COLOR = {
  new: "bg-brand-orange/15 text-brand-orange",
  contacted: "bg-brand-teal/15 text-brand-teal",
  converted: "bg-green-100 text-green-700",
  closed: "bg-gray-200 text-gray-600",
};

const PAY_COLOR = {
  success: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-600",
  flagged: "bg-amber-100 text-amber-700",
  initiated: "bg-gray-200 text-gray-600",
};

function PaymentLinkGenerator() {
  const [f, setF] = useState({ amount: "", purpose: "", name: "", phone: "" });
  const link = f.amount
    ? `${window.location.origin}/pay?amount=${encodeURIComponent(f.amount)}${f.purpose ? `&purpose=${encodeURIComponent(f.purpose)}` : ""}${f.name ? `&name=${encodeURIComponent(f.name)}` : ""}${f.phone ? `&phone=${encodeURIComponent(f.phone)}` : ""}`
    : "";
  const waMsg = `Namaste${f.name ? ` ${f.name} ji` : ""}! Cluetrip Holidays ki taraf se payment link:\n\n💳 Amount: ₹${f.amount}${f.purpose ? `\n📦 ${f.purpose}` : ""}\n\nIs link par click karke Card/UPI/NetBanking se pay karein:\n${link}\n\nDhanyavaad! 🙏`;
  const copyLink = () => {
    const ta = document.createElement("textarea");
    ta.value = link; ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.select();
    try { document.execCommand("copy"); toast.success("Payment link copied!"); } catch { toast.error("Copy failed"); }
    document.body.removeChild(ta);
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(link).catch(() => {});
  };
  const inputCls = "rounded-lg border border-brand-sand bg-brand-cream px-4 py-3 text-sm outline-none focus:border-brand-teal";
  return (
    <div className="rounded-2xl bg-white border-2 border-brand-orange/40 p-5 mb-8" data-testid="payment-link-generator">
      <p className="font-heading text-xl text-brand-navy mb-1">🔗 Payment Link Generator</p>
      <p className="text-xs text-[#8B89A4] mb-4">Amount daalo, link banao, customer ko WhatsApp karo — wo click karke seedha PayU se pay karega</p>
      <div className="grid sm:grid-cols-4 gap-3">
        <input type="number" min="1" value={f.amount} onChange={(e) => setF((x) => ({ ...x, amount: e.target.value }))} placeholder="Amount (₹) *" data-testid="paylink-amount" className={inputCls} />
        <input value={f.purpose} onChange={(e) => setF((x) => ({ ...x, purpose: e.target.value }))} placeholder="Package / purpose" data-testid="paylink-purpose" className={inputCls} />
        <input value={f.name} onChange={(e) => setF((x) => ({ ...x, name: e.target.value }))} placeholder="Customer name" data-testid="paylink-name" className={inputCls} />
        <input value={f.phone} onChange={(e) => setF((x) => ({ ...x, phone: e.target.value }))} placeholder="Customer WhatsApp no." data-testid="paylink-phone" className={inputCls} />
      </div>
      {link && (
        <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <p className="flex-1 rounded-lg bg-brand-cream border border-brand-sand px-4 py-3 text-xs font-mono text-brand-navy break-all" data-testid="paylink-url">{link}</p>
          <button onClick={copyLink} data-testid="paylink-copy" className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-teal px-5 py-3 text-xs font-bold text-white"><CopySimple size={15} /> Copy Link</button>
          <a
            href={`https://wa.me/${f.phone ? `91${f.phone.replace(/\D/g, "").slice(-10)}` : ""}?text=${encodeURIComponent(waMsg)}`}
            target="_blank" rel="noreferrer" data-testid="paylink-whatsapp"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3 text-xs font-bold text-white"
          >
            <WhatsappLogo size={15} weight="fill" /> Send on WhatsApp
          </a>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState({ packages: 0, inquiries: 0, new_inquiries: 0 });
  const [packages, setPackages] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [payments, setPayments] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [offers, setOffers] = useState([]);
  const [galleryForm, setGalleryForm] = useState({ image: "", caption: "" });
  const [offerForm, setOfferForm] = useState({ title: "", subtitle: "", discount_text: "", link_query: "" });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/packages").then((r) => setPackages(r.data));
    api.get("/inquiries").then((r) => setInquiries(r.data));
    api.get("/payments").then((r) => setPayments(r.data)).catch(() => {});
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
    api.get("/offers/all").then((r) => setOffers(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const doLogout = () => { logout(); navigate("/admin"); };

  const deletePackage = async (id) => {
    if (!window.confirm("Delete this package?")) return;
    await api.delete(`/packages/${id}`);
    toast.success("Package deleted");
    load();
  };

  const setStatus = async (id, status) => {
    await api.patch(`/inquiries/${id}`, { status });
    setInquiries((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
    api.get("/stats").then((r) => setStats(r.data));
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    await api.delete(`/inquiries/${id}`);
    toast.success("Enquiry deleted");
    load();
  };

  const addGalleryItem = async (e) => {
    e.preventDefault();
    if (!galleryForm.image) return toast.error("Image URL required");
    await api.post("/gallery", galleryForm);
    toast.success("Photo added");
    setGalleryForm({ image: "", caption: "" });
    api.get("/gallery").then((r) => setGallery(r.data));
  };

  const deleteGalleryItem = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    await api.delete(`/gallery/${id}`);
    setGallery((g) => g.filter((x) => x.id !== id));
  };

  const addOffer = async (e) => {
    e.preventDefault();
    if (!offerForm.title) return toast.error("Offer title required");
    await api.post("/offers", offerForm);
    toast.success("Offer created");
    setOfferForm({ title: "", subtitle: "", discount_text: "", link_query: "" });
    api.get("/offers/all").then((r) => setOffers(r.data));
  };

  const toggleOffer = async (o) => {
    await api.patch(`/offers/${o.id}`, { active: !o.active });
    setOffers((prev) => prev.map((x) => (x.id === o.id ? { ...x, active: !o.active } : x)));
  };

  const deleteOffer = async (id) => {
    if (!window.confirm("Delete this offer?")) return;
    await api.delete(`/offers/${id}`);
    setOffers((prev) => prev.filter((x) => x.id !== id));
  };

  const NAV = [
    { id: "overview", label: "Overview", icon: SquaresFour },
    { id: "packages", label: "Packages", icon: PackageIcon },
    { id: "inquiries", label: "Enquiries", icon: EnvelopeSimple },
    { id: "payments", label: "Payments", icon: CreditCard },
    { id: "gallery", label: "Gallery", icon: Images },
    { id: "offers", label: "Offers", icon: Megaphone },
  ];

  return (
    <div className="min-h-screen bg-brand-sand flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col bg-brand-navy text-white/70 p-6 shrink-0">
        <div className="bg-white rounded-xl p-2.5 mb-10"><img src="/assets/cluetrip-logo.png" alt="Cluetrip" className="h-9 w-auto" /></div>
        <nav className="space-y-1.5 flex-1">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => setTab(n.id)}
              data-testid={`admin-nav-${n.id}`}
              className={`flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors ${tab === n.id ? "bg-brand-teal text-white" : "hover:bg-white/10 hover:text-white"}`}
            >
              <n.icon size={20} weight={tab === n.id ? "fill" : "regular"} /> {n.label}
            </button>
          ))}
        </nav>
        <button onClick={doLogout} data-testid="admin-logout" className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium hover:bg-white/10 hover:text-white transition-colors"><SignOut size={20} /> Logout</button>
      </aside>

      <main className="flex-1 p-5 md:p-10 overflow-x-hidden">
        {/* Mobile tabs */}
        <div className="md:hidden flex gap-2 mb-6">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => setTab(n.id)} className={`flex-1 rounded-lg px-3 py-2.5 text-xs font-semibold ${tab === n.id ? "bg-brand-teal text-white" : "bg-white text-brand-navy"}`}>{n.label}</button>
          ))}
        </div>

        {tab === "overview" && (
          <div data-testid="admin-overview">
            <h1 className="font-heading text-4xl text-brand-navy mb-2">Dashboard</h1>
            <p className="text-[#8B89A4] mb-8">Welcome back, Anuj.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { label: "Total Packages", value: stats.packages, icon: PackageIcon },
                { label: "Total Enquiries", value: stats.inquiries, icon: EnvelopeSimple },
                { label: "New Enquiries", value: stats.new_inquiries, icon: Phone },
                { label: "Payments Received", value: `₹${payments.filter((p) => p.status === "success").reduce((s, p) => s + (p.amount || 0), 0).toLocaleString("en-IN")}`, icon: CreditCard },
              ].map((c) => (
                <div key={c.label} className="rounded-2xl bg-white p-7 border border-brand-sand">
                  <c.icon size={28} className="text-brand-teal mb-4" weight="duotone" />
                  <p className="font-heading text-4xl text-brand-navy">{c.value}</p>
                  <p className="text-sm text-[#8B89A4] mt-1">{c.label}</p>
                </div>
              ))}
            </div>
            <h2 className="font-heading text-2xl text-brand-navy mt-10 mb-4">Latest enquiries</h2>
            <div className="rounded-2xl bg-white border border-brand-sand overflow-hidden">
              {inquiries.slice(0, 5).map((i) => (
                <div key={i.id} className="flex items-center justify-between px-6 py-4 border-b border-brand-sand last:border-0">
                  <div><p className="font-medium text-brand-navy">{i.name}</p><p className="text-sm text-[#8B89A4]">{i.destination || "—"} · {i.phone}</p></div>
                  <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase ${STATUS_COLOR[i.status]}`}>{i.status}</span>
                </div>
              ))}
              {inquiries.length === 0 && <p className="px-6 py-8 text-center text-[#8B89A4]">No enquiries yet.</p>}
            </div>
          </div>
        )}

        {tab === "packages" && (
          <div data-testid="admin-packages">
            <div className="flex items-center justify-between mb-8">
              <h1 className="font-heading text-4xl text-brand-navy">Packages</h1>
              <button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-package-btn" className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-orangedark"><Plus size={18} weight="bold" /> Add package</button>
            </div>
            <div className="rounded-2xl bg-white border border-brand-sand overflow-hidden">
              <div className="grid grid-cols-12 px-6 py-3 bg-brand-sand text-xs font-bold uppercase tracking-wider text-[#8B89A4]">
                <span className="col-span-5">Package</span><span className="col-span-3">Region</span><span className="col-span-2">Price</span><span className="col-span-2 text-right">Actions</span>
              </div>
              {packages.map((p) => (
                <div key={p.id} className="grid grid-cols-12 items-center px-6 py-4 border-b border-brand-sand last:border-0" data-testid={`admin-pkg-${p.id}`}>
                  <div className="col-span-5 flex items-center gap-3">
                    <img src={p.image} alt="" className="h-11 w-11 rounded-lg object-cover" />
                    <div><p className="font-medium text-brand-navy leading-tight">{p.title}</p><p className="text-xs text-[#8B89A4]">{p.country_state}{p.featured && " · ★ Featured"}</p></div>
                  </div>
                  <span className="col-span-3 text-sm text-[#6b6a8a]">{p.region}</span>
                  <span className="col-span-2 text-sm font-semibold text-brand-orange">{inr(p.price)}</span>
                  <div className="col-span-2 flex justify-end gap-2">
                    <button onClick={() => { setEditing(p); setFormOpen(true); }} data-testid={`edit-pkg-${p.id}`} className="p-2 rounded-lg hover:bg-brand-sand text-brand-navy"><PencilSimple size={18} /></button>
                    <button onClick={() => deletePackage(p.id)} data-testid={`delete-pkg-${p.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash size={18} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "inquiries" && (
          <div data-testid="admin-inquiries">
            <h1 className="font-heading text-4xl text-brand-navy mb-8">Enquiries</h1>
            <div className="space-y-4">
              {inquiries.map((i) => (
                <div key={i.id} className="rounded-2xl bg-white border border-brand-sand p-6" data-testid={`inquiry-${i.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <p className="font-heading text-xl text-brand-navy">{i.name}</p>
                      <p className="text-sm text-[#8B89A4] mt-0.5">{new Date(i.created_at).toLocaleString("en-IN")}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <select value={i.status} onChange={(e) => setStatus(i.id, e.target.value)} data-testid={`status-${i.id}`} className={`rounded-full px-3 py-1.5 text-xs font-bold uppercase border-0 outline-none cursor-pointer ${STATUS_COLOR[i.status]}`}>
                        {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <button onClick={() => deleteInquiry(i.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash size={18} /></button>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 mt-4 text-sm">
                    <p><span className="text-[#8B89A4]">Phone:</span> <a href={`tel:${i.phone}`} className="text-brand-teal font-medium">{i.phone}</a></p>
                    <p><span className="text-[#8B89A4]">Email:</span> <a href={`mailto:${i.email}`} className="text-brand-teal font-medium break-all">{i.email}</a></p>
                    <p><span className="text-[#8B89A4]">Destination:</span> {i.destination || "—"}</p>
                    <p><span className="text-[#8B89A4]">Travellers:</span> {i.travelers || "—"}</p>
                    <p><span className="text-[#8B89A4]">Travel date:</span> {i.travel_date || "—"}</p>
                    {i.package_title && <p><span className="text-[#8B89A4]">Package:</span> {i.package_title}</p>}
                  </div>
                  {i.message && <p className="mt-3 rounded-lg bg-brand-sand p-3 text-sm text-[#5b5980]">{i.message}</p>}
                </div>
              ))}
              {inquiries.length === 0 && <p className="text-center text-[#8B89A4] py-16">No enquiries yet.</p>}
            </div>
          </div>
        )}
        {tab === "payments" && (
          <div data-testid="admin-payments">
            <h1 className="font-heading text-4xl text-brand-navy mb-8">Payments</h1>
            <PaymentLinkGenerator />
            <div className="rounded-2xl bg-white border border-brand-sand overflow-hidden">
              <div className="hidden md:grid grid-cols-12 px-6 py-3 bg-brand-sand text-xs font-bold uppercase tracking-wider text-[#8B89A4]">
                <span className="col-span-3">Customer</span><span className="col-span-4">Package</span><span className="col-span-2">Amount</span><span className="col-span-2">Date</span><span className="col-span-1">Status</span>
              </div>
              {payments.map((p) => (
                <div key={p.id} className="grid grid-cols-2 md:grid-cols-12 gap-2 items-center px-6 py-4 border-b border-brand-sand last:border-0" data-testid={`payment-row-${p.txnid}`}>
                  <div className="col-span-2 md:col-span-3">
                    <p className="font-medium text-brand-navy">{p.name}</p>
                    <p className="text-xs text-[#8B89A4]">{p.phone} · {p.email}</p>
                    <p className="text-[10px] font-mono text-[#a3a1ba]">{p.txnid}</p>
                  </div>
                  <p className="col-span-2 md:col-span-4 text-sm text-[#6b6a8a]">{p.package_title}</p>
                  <div className="md:col-span-2">
                    <p className="text-sm font-bold text-brand-orange">{inr(p.amount)}</p>
                    <p className="text-[10px] text-[#8B89A4]">{p.advance_percent}% of {inr(p.total_price)}</p>
                  </div>
                  <p className="md:col-span-2 text-xs text-[#8B89A4]">{new Date(p.created_at).toLocaleString("en-IN")}</p>
                  <span className={`md:col-span-1 w-fit rounded-full px-3 py-1 text-[10px] font-bold uppercase ${PAY_COLOR[p.status] || PAY_COLOR.initiated}`}>{p.status}</span>
                </div>
              ))}
              {payments.length === 0 && <p className="px-6 py-12 text-center text-[#8B89A4]">No payments yet.</p>}
            </div>
          </div>
        )}
        {tab === "gallery" && (
          <div data-testid="admin-gallery">
            <h1 className="font-heading text-4xl text-brand-navy mb-8">Gallery</h1>
            <form onSubmit={addGalleryItem} className="rounded-2xl bg-white border border-brand-sand p-5 mb-8 flex flex-col sm:flex-row gap-3">
              <input value={galleryForm.image} onChange={(e) => setGalleryForm((f) => ({ ...f, image: e.target.value }))} placeholder="Photo URL (https://...)" data-testid="gallery-image-input"
                className="flex-1 rounded-lg border border-brand-sand bg-brand-cream px-4 py-3 text-sm outline-none focus:border-brand-teal" />
              <input value={galleryForm.caption} onChange={(e) => setGalleryForm((f) => ({ ...f, caption: e.target.value }))} placeholder="Caption (optional)" data-testid="gallery-caption-input"
                className="sm:w-64 rounded-lg border border-brand-sand bg-brand-cream px-4 py-3 text-sm outline-none focus:border-brand-teal" />
              <button type="submit" data-testid="gallery-add-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-3 text-sm font-semibold text-white hover:bg-brand-orangedark"><Plus size={16} weight="bold" /> Add Photo</button>
            </form>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {gallery.map((g) => (
                <div key={g.id} className="group relative overflow-hidden rounded-xl" data-testid={`admin-gallery-${g.id}`}>
                  <img src={g.image} alt={g.caption} className="h-40 w-full object-cover" />
                  {g.caption && <p className="absolute bottom-0 inset-x-0 bg-brand-navy/70 text-white text-xs p-2 truncate">{g.caption}</p>}
                  <button onClick={() => deleteGalleryItem(g.id)} data-testid={`delete-gallery-${g.id}`} className="absolute top-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"><Trash size={15} /></button>
                </div>
              ))}
              {gallery.length === 0 && <p className="col-span-full text-center text-[#8B89A4] py-12">No photos yet. Add your first trip photo above.</p>}
            </div>
          </div>
        )}

        {tab === "offers" && (
          <div data-testid="admin-offers">
            <h1 className="font-heading text-4xl text-brand-navy mb-8">Seasonal Offers</h1>
            <form onSubmit={addOffer} className="rounded-2xl bg-white border border-brand-sand p-5 mb-8 grid sm:grid-cols-2 gap-3">
              <input value={offerForm.title} onChange={(e) => setOfferForm((f) => ({ ...f, title: e.target.value }))} placeholder="Offer title * (e.g., Summer Special — Manali)" data-testid="offer-title-input"
                className="rounded-lg border border-brand-sand bg-brand-cream px-4 py-3 text-sm outline-none focus:border-brand-teal" />
              <input value={offerForm.discount_text} onChange={(e) => setOfferForm((f) => ({ ...f, discount_text: e.target.value }))} placeholder="Discount badge (e.g., 15% OFF)" data-testid="offer-discount-input"
                className="rounded-lg border border-brand-sand bg-brand-cream px-4 py-3 text-sm outline-none focus:border-brand-teal" />
              <input value={offerForm.subtitle} onChange={(e) => setOfferForm((f) => ({ ...f, subtitle: e.target.value }))} placeholder="Subtitle (e.g., Valid till 30 June)" data-testid="offer-subtitle-input"
                className="rounded-lg border border-brand-sand bg-brand-cream px-4 py-3 text-sm outline-none focus:border-brand-teal" />
              <input value={offerForm.link_query} onChange={(e) => setOfferForm((f) => ({ ...f, link_query: e.target.value }))} placeholder="Link search (e.g., Manali)" data-testid="offer-link-input"
                className="rounded-lg border border-brand-sand bg-brand-cream px-4 py-3 text-sm outline-none focus:border-brand-teal" />
              <button type="submit" data-testid="offer-add-btn" className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-3 text-sm font-semibold text-white hover:bg-brand-orangedark"><Plus size={16} weight="bold" /> Create Offer</button>
            </form>
            <div className="space-y-3">
              {offers.map((o) => (
                <div key={o.id} className="rounded-2xl bg-white border border-brand-sand p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`admin-offer-${o.id}`}>
                  <div>
                    <p className="font-heading text-xl text-brand-navy">{o.title} {o.discount_text && <span className="ml-2 rounded-full bg-brand-orange/15 text-brand-orange px-3 py-0.5 text-xs font-bold">{o.discount_text}</span>}</p>
                    <p className="text-sm text-[#8B89A4] mt-0.5">{o.subtitle || "—"} {o.link_query && <span className="ml-2 text-brand-teal">→ /packages?q={o.link_query}</span>}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => toggleOffer(o)} data-testid={`toggle-offer-${o.id}`}
                      className={`rounded-full px-4 py-1.5 text-xs font-bold uppercase ${o.active ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"}`}>
                      {o.active ? "Live on Homepage" : "Hidden"}
                    </button>
                    <button onClick={() => deleteOffer(o.id)} data-testid={`delete-offer-${o.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash size={18} /></button>
                  </div>
                </div>
              ))}
              {offers.length === 0 && <p className="text-center text-[#8B89A4] py-12">No offers yet. Create one — it will show on the homepage instantly.</p>}
            </div>
          </div>
        )}
      </main>

      {formOpen && <PackageForm initial={editing} onClose={() => setFormOpen(false)} onSaved={() => { setFormOpen(false); load(); }} />}
    </div>
  );
}
